var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLoader } from 'ziphy-web-shared/basic/hooks';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { useMobxForm } from 'ziphy-web-shared/basic/lib/forms';
import { Textarea } from 'ziphy-web-shared/basic/lib/forms/fields';
import { Modal, ModalToolbarBtn } from 'ziphy-web-shared/basic/lib/utilities/modals';
import { $loader } from 'ziphy-web-shared/basic/utils';
import { createCodeableConcept, getFhirRef, getResourceProperties, isEmptyCode, isEnteredInErrorStatus, } from '../';
import SearchResourceInput from '../lib/SearchResourceInput';
const TaskReferralModal = ({ encounterId, resourceContext, resourceId: _resourceId, searchTaxonomy, canChangeCode = false, title, _core, }) => {
    const { t } = useTranslation();
    const [data, setData] = useState();
    const [resourceId, setResourceId] = useState(_resourceId);
    const isEdit = useMemo(() => Boolean(_resourceId), [_resourceId]);
    const [isChangeCode, setIsChangeCode] = useState(null);
    const isDisabled = useMemo(() => Boolean(searchTaxonomy) || (isEdit && !isChangeCode), [searchTaxonomy, isEdit, isChangeCode]);
    const { service } = useMemo(() => getResourceProperties(resourceContext), [resourceContext]);
    const { isLoading } = useLoader(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!resourceId) {
            if (searchTaxonomy)
                setData({ performerType: createCodeableConcept(searchTaxonomy) });
            return;
        }
        const response = yield service.read({
            encounterId,
            fhirRef: getFhirRef(resourceContext.resourceType, resourceId),
            elementName: resourceContext.element,
        });
        if (!response.error) {
            setData(response.prepared);
            if (isChangeCode === null) {
                setIsChangeCode(isEmptyCode(response.prepared.performerType) || canChangeCode);
            }
        }
    }), [resourceId]);
    const form = useMobxForm(useMemo(() => ({
        initialValues: data,
        fieldsConfig: {
            performerType: { validate: ['required'] },
            note: { transformOut: ['emptyNull'] },
        },
    }), [data]));
    const performerType = form.field('performerType', {});
    const note = form.field('note', '');
    const isUpdate = resourceId || isChangeCode;
    const handleSubmit = form.submit((data) => __awaiter(void 0, void 0, void 0, function* () {
        const payload = {
            encounterId,
            elementName: resourceContext.element,
            data: data,
        };
        if (isUpdate) {
            payload.data.id = isChangeCode ? _resourceId : resourceId;
            const response = yield service.save(payload);
            if (!response.error) {
                yield _core.onHide(true, {
                    resourceId: response.prepared.id,
                    action: isEnteredInErrorStatus(data.status) ? 'DELETE' : 'UPDATE',
                });
            }
        }
        else {
            const response = yield service.create(payload);
            if (!response.error) {
                yield _core.onHide(true, {
                    resourceId: response.prepared.id,
                    action: 'CREATE',
                });
            }
        }
    }), { onlyChanged: isUpdate });
    return (_jsxs(Modal, Object.assign({ size: 'xl', centered: true, scrollable: true }, { children: [_jsx("div", Object.assign({ className: "modal-toolbar" }, { children: _jsx(ModalToolbarBtn, { type: "close", action: _core.onHide }) })), _jsx(Modal.Header, { children: title || isEdit ? t('label.referral') : t('label.add_referral') }), _jsx(Modal.Body, Object.assign({ isLoading: isLoading }, { children: _jsx("form", Object.assign({ id: form.id, onSubmit: $loader.registerHandler(form.id, handleSubmit) }, { children: _jsxs("div", Object.assign({ className: "row row--gutters-5 row--mb-10" }, { children: [_jsx("div", Object.assign({ className: "col-12" }, { children: _jsx(SearchResourceInput, { field: performerType, errorMessage: performerType.error, setResourceId: setResourceId, encounterId: encounterId, resourceContext: resourceContext, placeholder: t('label.search_by_name_or_code'), mode: ['md'], disabled: isDisabled, clear: !isDisabled }) })), _jsx("div", Object.assign({ className: "col-12" }, { children: _jsx(Textarea, { field: note, mode: ['sm'], label: t('label.note'), placeholder: t('label.any_details'), rows: 5 }) }))] })) })) })), _jsxs(Modal.Footer, Object.assign({ className: 'gap-10' }, { children: [_jsx(Button, Object.assign({ mode: ['primary', 'sm', 'min110'], form: form.id, isLoading: $loader.isRunHandler(form.id), disabled: isLoading }, { children: t('btn.save') })), _jsx(Button, Object.assign({ mode: ['secondary', 'sm', 'min110'], disabled: isLoading, onClick: () => _core.onHide() }, { children: t('btn.cancel') }))] }))] })));
};
TaskReferralModal.displayName = 'TaskReferralModal';
export default observer(TaskReferralModal);
